import { defineStore } from 'pinia'
import type { Coupon } from '~/composables/types/checkout'
import type { Price } from '~/composables/types/event'
import { getItemsForOrder } from '~/utils/cartGetters'
import { useNotificationsStore } from '../stores/notifications'

export interface CouponValidateResponse {
  coupon: Coupon
  discount: null | any
}

export const useSessionVoucherStore = defineStore('sessionVoucher', () => {
  const sessionVoucherCookie = useCookie('sessionVoucher')
  const { addNotification, deleteNotification } = useNotificationsStore()
  const { $i18n } = useNuxtApp()
  const { locale, t } = $i18n
  const voucherData = ref<Coupon | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const { cart, loadFromStorage } = useCartWithSubdomainLogic()

  const ensureCartLoaded = () => {
    loadFromStorage()
  }

  const getDiscountAmount = (coupon: Coupon) => {
    if (coupon?.discount_type === 'FIXED_VALUE') {
      return formatPriceAmount(
        Number((coupon.value as Price).amount),
        locale.value
      )
    } else if (coupon?.discount_type === 'PERCENTAGE_VALUE') {
      return coupon.value + '%'
    }
    return undefined
  }

  const showVoucherNotification = () => {
    if (!import.meta.client || !voucherData.value) return

    const amount = getDiscountAmount(voucherData.value)

    if (amount) {
      const fromQuery = Boolean(sessionVoucherCookie.value)

      let message = ''
      if (fromQuery) {
        message =
          voucherData.value?.success_message ||
          t('checkout.sessionVoucher.successMessage', { amount })
      } else {
        message = t('checkout.sessionVoucher.reminderMessage', { amount })
      }

      const today = new Date().getTime()
      const closedNotifications = JSON.parse(
        localStorage.getItem('closedNotifications') || '{}'
      ) as Record<string, number>

      const notificationId = 'session-voucher-' + sessionVoucherCookie.value

      if (
        !closedNotifications[notificationId] ||
        closedNotifications[notificationId] < today
      ) {
        addNotification({
          id: notificationId,
          type: 'SUCCESS',
          message,
          showIcon: true,
          persist: true,
        })
      }
    }
  }

  const validateVoucher = async (
    voucherCode: string,
    supplier_id?: string,
    shippingAddress?: any
  ) => {
    if (!import.meta.client) return

    loading.value = true
    error.value = null

    try {
      ensureCartLoaded()

      // Same predicate as `source`: EMBEDDED iff root `supplier_id` is set (supplier-scoped).
      const supplierScoped = Boolean(supplier_id)
      const external = supplierScoped

      const items = getItemsForOrder(cart.value, {
        external,
        bookingGiftcardSupplierFallback: supplierScoped
          ? String(supplier_id)
          : undefined,
      })
      const response = await kftApiRequest<CouponValidateResponse>(
        `v1/store/coupon/${voucherCode}/validate`,
        {
          method: 'POST',
          body: {
            supplier_id: supplier_id || undefined,
            external,
            items,
            source: supplier_id ? 'EMBEDDED' : 'KONFETTI',
            address: shippingAddress || undefined,
          },
        }
      )

      const coupon = response?.data?.coupon
      const discount = response?.data?.discount

      if (!coupon || !coupon.value) {
        throw new Error("Coupon doesn't exist or has no value")
      }

      voucherData.value = coupon
      voucherData.value.discount = discount
      sessionVoucherCookie.value = voucherCode

      return coupon
    } catch (e: any) {
      if (e?.status === 403) {
        clearVoucher()
      }
      error.value = e?.data?.message || 'Failed to validate voucher'
      throw e
    } finally {
      loading.value = false
    }
  }

  const clearVoucher = () => {
    if (import.meta.client) {
      const closedNotifications = JSON.parse(
        localStorage.getItem('closedNotifications') || '{}'
      ) as Record<string, number>

      const notificationId = 'session-voucher-' + sessionVoucherCookie.value
      deleteNotification(notificationId)

      if (closedNotifications?.[notificationId] != null) {
        const next = Object.fromEntries(
          Object.entries(closedNotifications).filter(
            ([k]) => k !== notificationId
          )
        ) as Record<string, number>

        localStorage.setItem('closedNotifications', JSON.stringify(next))
      }
    }

    sessionVoucherCookie.value = null
    voucherData.value = null
    error.value = null
  }

  // Initialize voucher data if cookie exists
  if (import.meta.client && sessionVoucherCookie.value) {
    validateVoucher(sessionVoucherCookie.value, undefined, undefined)
  }

  const minimumAmountForCoupon = computed(() => {
    if (!voucherData.value) return 0
    return Number(voucherData.value.minimum_order_value?.amount) / 100
  })

  const couponError = computed(() => {
    if (!voucherData.value) return null

    const cartTotal = cartGetters.getTotals(cart.value).total

    if (
      minimumAmountForCoupon.value &&
      cartTotal < minimumAmountForCoupon.value
    ) {
      return 'minimum-not-reached'
    }
    return null
  })

  return {
    voucherCode: computed(() => sessionVoucherCookie.value),
    voucherData: computed(() => voucherData.value),
    loading: computed(() => loading.value),
    error: computed(() => error.value),
    minimumAmountForCoupon,
    couponError,
    validateVoucher,
    clearVoucher,
    getDiscountAmount,
    showVoucherNotification,
  }
})
